import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal for a signed-in consumer to report an issue with a vendor.
 * Collects: Subject, Details, Desired Outcome.
 * POSTs to /api/complaints/report.
 */
export default function ReportIssueModal({ open, onClose, vendorId, vendorName }) {
  const { user, getAuthHeaders } = useAuth();
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [desiredOutcome, setDesiredOutcome] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setSubject('');
    setDetails('');
    setDesiredOutcome('');
    setDone(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please log in to file a report.');
      return;
    }
    if (subject.trim().length < 4) {
      toast.error('Subject must be at least 4 characters');
      return;
    }
    if (details.trim().length < 20) {
      toast.error('Please describe the issue in more detail (min 20 chars)');
      return;
    }
    if (desiredOutcome.trim().length < 4) {
      toast.error('Tell us what resolution you\'d like');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/complaints/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          vendor_id: vendorId,
          subject: subject.trim(),
          details: details.trim(),
          desired_outcome: desiredOutcome.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(true);
        toast.success('Report submitted — our team will review it.');
      } else {
        toast.error(data?.detail || 'Failed to submit report');
      }
    } catch {
      toast.error('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={handleClose}
          data-testid="report-issue-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-[#1A1A3E] border border-white/10 rounded-2xl p-6 max-w-lg w-full shadow-[0_20px_60px_rgba(0,0,0,0.5)] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#FCD34D]" />
                </div>
                <div>
                  <h2 className="font-syne text-lg font-bold text-white">Report an issue</h2>
                  <p className="text-[#9CA3AF] text-xs mt-0.5">
                    {vendorName ? `Regarding ${vendorName}` : 'Anonymous to the vendor'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/5"
                data-testid="close-report-modal"
              >
                <X size={18} />
              </button>
            </div>

            {done ? (
              <div className="text-center py-8" data-testid="report-submitted">
                <div className="w-14 h-14 rounded-2xl bg-green-500/15 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-[#86EFAC]" />
                </div>
                <h3 className="font-syne text-white font-bold mb-2">Report submitted</h3>
                <p className="text-[#9CA3AF] text-sm mb-6 max-w-sm mx-auto">
                  Thanks for speaking up. Our trust & safety team will review this within 1-3 business days and email you if we need more info.
                </p>
                <button
                  onClick={handleClose}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-sm font-semibold"
                  data-testid="close-after-submit"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-[#9CA3AF] text-xs mb-1 block">Subject *</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="One-line summary (e.g., 'Vendor never delivered product')"
                    maxLength={200}
                    className="bg-white/5 border-white/10 text-white rounded-xl h-11"
                    data-testid="report-subject"
                  />
                </div>

                <div>
                  <Label className="text-[#9CA3AF] text-xs mb-1 block">What happened? *</Label>
                  <Textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Walk us through what happened — the more detail the better. Include dates, amounts, and any order IDs you have."
                    maxLength={4000}
                    className="bg-white/5 border-white/10 text-white rounded-xl min-h-[140px]"
                    data-testid="report-details"
                  />
                  <p className="text-[#64748B] text-[10px] mt-1">{details.length}/4000 · min 20 characters</p>
                </div>

                <div>
                  <Label className="text-[#9CA3AF] text-xs mb-1 block">What resolution would you like? *</Label>
                  <Textarea
                    value={desiredOutcome}
                    onChange={(e) => setDesiredOutcome(e.target.value)}
                    placeholder="Refund, re-delivery, apology, account review, etc."
                    maxLength={1000}
                    className="bg-white/5 border-white/10 text-white rounded-xl min-h-[80px]"
                    data-testid="report-desired-outcome"
                  />
                </div>

                <div className="p-3 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/20">
                  <p className="text-[#FCD34D] text-xs">
                    Reports are reviewed by our trust & safety team. False or malicious reports may result in account action.
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-sm font-semibold disabled:opacity-50"
                    data-testid="submit-report-btn"
                  >
                    {submitting ? 'Submitting…' : 'Submit report'}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-xl border border-white/10 text-[#9CA3AF] text-sm hover:text-white hover:bg-white/5"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
